import {gql} from '@apollo/client';
import React, {useState} from 'react';

import * as Analytics from 'analytics/Analytics';
import {makeAnalyticsEventId} from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import isArchived from 'archive/isArchived';
import BenchlingConfig from 'BenchlingConfig';
import {
  CREATE_PROCESS_MODAL_TYPE,
  WORKFLOWS_ANALYTICS_ACTIONS,
} from 'constants/analytics-products/WorkflowAnalyticsConstants';
import {ActionOrigin, ANALYTICS_MODULE, COMMON_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {MinimalStorableSummaryWithSchemaId} from 'constants/SampleConstants';
import {ProcessPlatformFeatureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {doQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import {BioentitySummary} from 'graphql-client/declarative-translations/external/RESTTypes';
import {SchemaFieldSelectorsDict} from 'notebook/structured-tables/hooks/useSelectorsById';
import fetchSelectorsById from 'notebook/structured-tables/live-data/fetchSelectorsById';
import {getStructuredTableAdapters} from 'notebook/structured-tables/StructuredTableAdapters';
import {
  StructuredTable,
  StructuredTableKind,
  UnknownStructuredTable,
  UnknownStructuredTableAdapter,
} from 'notebook/structured-tables/StructuredTableConstants';
import {getSourceTypeForStructuredTable} from 'notebook/structured-tables/util/StructuredTableAnalyticsUtil';
import {EvaluatedCell} from 'notebook/tables/excess/Evaluator';
import {getSchemaDisplayNameForCreate} from 'processes/CreateProcessUtil';
import CreateProcessModal, {
  TableOrFormRenderOptionsToAnalyticsType,
} from 'processes/modals/CreateProcessModal';
import {getInitialCreateTaskModalViewPreference} from 'processes/modals/CreateProcessModalViewPreference';
import {
  CreateProcessButtonQuery,
  CreateProcessButtonQueryVariables,
} from 'processes/tables/create-process-button/__generated/CreateProcessButtonQuery';
import {ProcessOutputStructuredTable} from 'processes/tables/ProcessOutputStructuredTableAdapter';
import {ProcessTaskStructuredTable} from 'processes/tables/ProcessTaskStructuredTableAdapter';
import {userCreatableProcessTaskSchema} from 'processes/util/ProcessSchemaConfigUtil';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';
import bufferForUX from 'util/bufferForUX';

import {getSourceColumnsAndTypeFromStructuredTable} from '../column-mapping/getSourceColumnsAndTypeFromStructuredTable';
import {handleSelectSchema} from '../column-mapping/handleSelectSchema';
import mapItemsToTaskTable from '../column-mapping/mapItemsToTaskSchema';
import mapStructuredTableToTaskSchema from '../column-mapping/mapStructuredTableToTaskSchema';

export type ProcessTaskSchema =
  CreateProcessButtonQuery['processTaskSchemasSupportingPlatformFeature'][number];

const BUTTON_TITLE = 'Create workflow tasks';

export const CREATE_PROCESS_BUTTON_QUERY = gql`
  query CreateProcessButtonQuery($platformFeature: ProcessPlatformFeatureType!)
  @skipAudit
  @engComponent(name: WORKFLOWS) {
    processTaskSchemasSupportingPlatformFeature(platformFeature: $platformFeature) {
      ... on ProcessTaskSchemaNode {
        ...ProcessTaskSchemaGraphQL
        statusLifecycle {
          ... on ProcessTaskStatusLifecycleNode {
            ...ProcessTaskStatusLifecycleGraphQL
          }
        }
      }
    }
  }
  ${getFragmentWrapper('ProcessTaskSchema').fragment}
  ${getFragmentWrapper('ProcessTaskStatusLifecycle').fragment}
`;

interface SearchableProcessSchemaMenuProps {
  processTaskSchemas: Array<ProcessTaskSchema>;
  onSelect: (schema: ProcessTaskSchema) => void;
  style?: React.CSSProperties; // This is passed in via AbsoluteDropdownContainer
}

interface CreateProcessButtonFromTableProps {
  structuredTable: UnknownStructuredTable;
  structuredTableAdapter: UnknownStructuredTableAdapter;
  computedTable: Array<Array<EvaluatedCell>> | null;
}

interface CreateProcessButtonFromItemsProps {
  buttonStyle: ButtonStyle;
  buttonSize: ButtonSize;
  items: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>;
  disabled: boolean;
  sourceType: string;
  // This callback aims to track user clicks.
  onClickTracking?: () => void;
}

type CreateProcessButtonProps = CreateProcessButtonFromTableProps | CreateProcessButtonFromItemsProps;

function isCreatingFromStructuredTable(
  props: CreateProcessButtonProps
): props is CreateProcessButtonFromTableProps {
  return (props as CreateProcessButtonFromTableProps).structuredTable != null;
}

export function handleSelectSchemaFromItems(
  schema: ProcessTaskSchema,
  items: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>,
  selectorsById: SchemaFieldSelectorsDict,
  sourceType: string,
  onClickTracking?: () => void
): void {
  const {successfullyMappedColumn, taskTable, footerMessage} = mapItemsToTaskTable(
    schema,
    items,
    selectorsById
  );
  const analyticsEventGroupId = makeAnalyticsEventId();
  Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
    event_group_id: analyticsEventGroupId,
    action_origin: ActionOrigin.SEARCH_EXPANDED_SEARCH_TOOLBAR,
    default_task_creation_view:
      TableOrFormRenderOptionsToAnalyticsType[
        getInitialCreateTaskModalViewPreference(schema.id, taskTable)[0]
      ],
    modal_type: CREATE_PROCESS_MODAL_TYPE,
  });
  if (!successfullyMappedColumn) {
    Analytics.trackEventWithModule(
      WORKFLOWS_ANALYTICS_ACTIONS.FAILED_COLUMN_MAPPING_TO_TASK_TABLE,
      ANALYTICS_MODULE.WORKFLOWS,
      {
        event_group_id: analyticsEventGroupId,
        action_origin: ActionOrigin.SEARCH_EXPANDED_SEARCH_TOOLBAR,
        modal_type: CREATE_PROCESS_MODAL_TYPE,
      }
    );
  }
  onClickTracking?.();
  runModal(CreateProcessModal, {
    processTaskSchemaId: schema.id,
    processTaskSchemaName: schema.name,
    initialFolderId: null,
    taskGroupInstructions: schema.taskGroupInstructions,
    taskTableWithPropagatedContent: taskTable,
    sourceType,
    analyticsEventGroupId,
    footerMessage,
  });
}

async function handleSelectSchemaFromTable(
  schema: ProcessTaskSchema,
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>> | null,
  selectorsById: SchemaFieldSelectorsDict
): Promise<void> {
  let sourceLocation;
  const {successfullyMappedColumn, taskTable, footerMessage} = await mapStructuredTableToTaskSchema(
    schema,
    structuredTable,
    computedTable,
    selectorsById
  );

  // Determine creation sourceType for product analytics
  if (structuredTable.kind === StructuredTableKind.PROCESS_TASK) {
    sourceLocation = (structuredTable as ProcessTaskStructuredTable).metadata.tableLocation?.toLowerCase();
  } else if (structuredTable.kind === StructuredTableKind.PROCESS_OUTPUT) {
    sourceLocation = (structuredTable as ProcessOutputStructuredTable).metadata.tableLocation?.toLowerCase();
  } else {
    sourceLocation = 'entry';
  }
  const analyticsEventGroupId = makeAnalyticsEventId();
  Analytics.trackEventWithModule(COMMON_ANALYTICS_ACTIONS.OPENED_MODAL, ANALYTICS_MODULE.WORKFLOWS, {
    event_group_id: analyticsEventGroupId,
    action_origin: structuredTableActionOrigin(structuredTable),
    default_task_creation_view:
      TableOrFormRenderOptionsToAnalyticsType[
        getInitialCreateTaskModalViewPreference(schema.id, taskTable)[0]
      ],
    modal_type: CREATE_PROCESS_MODAL_TYPE,
  });
  if (!successfullyMappedColumn) {
    Analytics.trackEventWithModule(
      WORKFLOWS_ANALYTICS_ACTIONS.FAILED_COLUMN_MAPPING_TO_TASK_TABLE,
      ANALYTICS_MODULE.WORKFLOWS,
      {
        event_group_id: analyticsEventGroupId,
        action_origin: structuredTableActionOrigin(structuredTable),
        modal_type: CREATE_PROCESS_MODAL_TYPE,
      }
    );
  }
  runModal(CreateProcessModal, {
    processTaskSchemaId: schema.id,
    processTaskSchemaName: schema.name,
    initialFolderId: null,
    taskGroupInstructions: schema.taskGroupInstructions,
    taskTableWithPropagatedContent: taskTable,
    sourceType: `${sourceLocation}_${getSourceTypeForStructuredTable(structuredTable)}_shortcut`,
    analyticsEventGroupId,
    footerMessage,
  });
}

function SearchableProcessSchemaMenu(props: SearchableProcessSchemaMenuProps): JSX.Element {
  return (
    <SearchableMenu
      height={Menu.HEIGHTS.MEDIUM}
      width={Menu.WIDTHS.MEDIUM}
      onSelect={props.onSelect}
      optionDatas={props.processTaskSchemas.map((schema) => ({
        value: schema,
        name: getSchemaDisplayNameForCreate(schema, props.processTaskSchemas),
      }))}
      dropLeft={true}
      style={props.style}
    />
  );
}

function handleSelectSchemaFromItemsV2(
  schema: ProcessTaskSchema,
  items: Array<BioentitySummary> | Array<MinimalStorableSummaryWithSchemaId>,
  selectorsById: SchemaFieldSelectorsDict,
  sourceType: string,
  onClickTracking?: () => void
): void {
  handleSelectSchema(
    [{columnName: 'Selected items from search', columnValues: items, hasValues: true}],
    schema,
    selectorsById,
    sourceType,
    ActionOrigin.SEARCH_EXPANDED_SEARCH_TOOLBAR,
    onClickTracking
  );
}

function handleSelectSchemaFromTableV2(
  schema: ProcessTaskSchema,
  structuredTable: StructuredTable<unknown, unknown>,
  computedTable: Array<Array<EvaluatedCell>> | null,
  selectorsById: SchemaFieldSelectorsDict,
  onClickTracking?: () => void
): void {
  const adapter = getStructuredTableAdapters()[structuredTable.kind];
  const {sourceColumns, sourceType} = getSourceColumnsAndTypeFromStructuredTable(
    structuredTable,
    computedTable,
    adapter
  );
  handleSelectSchema(
    sourceColumns,
    schema,
    selectorsById,
    sourceType,
    structuredTableActionOrigin(structuredTable),
    onClickTracking,
    adapter,
    structuredTable
  );
}

export default function CreateProcessButton(props: CreateProcessButtonProps): JSX.Element {
  const [allSchemas, setAllSchemas] = useState<Array<ProcessTaskSchema> | null>(null);
  const [selectorsById, setSelectorsById] = useState<SchemaFieldSelectorsDict | null>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const getIsButtonDisabled = (): boolean => {
    if (isCreatingFromStructuredTable(props)) {
      // Lookup tables' rows are never finalized because they are just data lookups - they do not perform
      // actions that need to be submitted to the server.
      return (
        props.structuredTable.kind !== StructuredTableKind.LOOKUP &&
        !props.structuredTable.rows.some((row) => row.isFinalized)
      );
    }

    return props.disabled || props.items.length === 0;
  };

  const isButtonDisabled = getIsButtonDisabled();

  const onSelect = (schema: ProcessTaskSchema, selectorsById: SchemaFieldSelectorsDict): void => {
    if (isCreatingFromStructuredTable(props)) {
      if (BenchlingConfig.WORKFLOWS_ENABLE_NEW_TASK_CREATION_SHORTCUT) {
        handleSelectSchemaFromTableV2(schema, props.structuredTable, props.computedTable, selectorsById);
      } else {
        handleSelectSchemaFromTable(schema, props.structuredTable, props.computedTable, selectorsById);
      }
    } else if (BenchlingConfig.WORKFLOWS_ENABLE_NEW_TASK_CREATION_SHORTCUT) {
      handleSelectSchemaFromItemsV2(
        schema,
        props.items,
        selectorsById,
        props.sourceType,
        props.onClickTracking
      );
    } else {
      handleSelectSchemaFromItems(
        schema,
        props.items,
        selectorsById,
        props.sourceType,
        props.onClickTracking
      );
    }
    setIsDropdownOpen(false);
  };

  const buttonStyleProps = isCreatingFromStructuredTable(props)
    ? {size: ButtonSize.MINI, buttonStyle: ButtonStyle.TERTIARY_GRAY, className: 'u-mini-spacing-left'}
    : {
        size: props.buttonSize,
        buttonStyle: props.buttonStyle,
      };

  return (
    <Tooltip title={BUTTON_TITLE}>
      <Dropdown
        menu={
          allSchemas != null && selectorsById != null ? (
            <SearchableProcessSchemaMenu
              processTaskSchemas={allSchemas}
              onSelect={(schema: ProcessTaskSchema) => {
                onSelect(schema, selectorsById);
              }}
            />
          ) : (
            <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM} dropLeft={true}>
              <Option isDisabled={true}>Loading schemas...</Option>
            </Menu>
          )
        }
        onToggle={async () => {
          setIsDropdownOpen(!isDropdownOpen);
          let allSchemasFetch;
          let selectorsFetch;

          // Kick off fetches
          if (allSchemas == null) {
            allSchemasFetch = bufferForUX(
              doQuerySafe<CreateProcessButtonQuery, CreateProcessButtonQueryVariables>(
                {query: CREATE_PROCESS_BUTTON_QUERY},
                {variables: {platformFeature: ProcessPlatformFeatureType.CAN_CREATE_TASK_GROUPS}}
              )
            );
          }
          if (selectorsById == null) {
            selectorsFetch = bufferForUX(fetchSelectorsById(true));
          }

          // Process results
          if (allSchemasFetch) {
            const result = await allSchemasFetch;
            const instantiableSchemas = result.processTaskSchemasSupportingPlatformFeature.filter(
              (schema) =>
                !isArchived(schema) &&
                !schema.canOnlyBePartOfFlowchart &&
                userCreatableProcessTaskSchema(schema)
            );
            setAllSchemas(instantiableSchemas);
          }
          if (selectorsFetch) {
            const selectors = await selectorsFetch;
            setSelectorsById(selectors);
          }
        }}
        isOpen={isDropdownOpen}
      >
        <Button
          {...buttonStyleProps}
          disabled={isButtonDisabled}
          icon="process"
          iconAltText={BUTTON_TITLE}
          isDropdown={true}
        />
      </Dropdown>
    </Tooltip>
  );
}
